import React from 'react';

import {assert} from 'Asserts';
import {appURL} from 'benchling-apps/BenchlingAppDAO';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {userURL} from 'dao/UserDAO';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import organizationDAO from 'orgs/OrganizationDAO';
import {teamURL} from 'orgs/TeamDAO';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

import {RoutineChipGraphQL} from '../../../graphql-fragments/llm/__generated/RoutineChipGraphQL';
import {PartyChipGraphQL} from '../../../graphql-fragments/parties/__generated/PartyChipGraphQL';
import {getSharedSmartChipProps, SharedSmartChipProps} from './ChipUtil';
import SmartChip from './SmartChip';
import useSmartChipQuery, {SmartChipType} from './useSmartChipQuery';

type OrganizationNode = ExtractByTypename<PartyChipGraphQL, 'OrganizationNode'>;
type PartyChipItem = PartyChipGraphQL | RoutineChipGraphQL;

const PARTY_TYPE_TO_URL_FN: {[k: string]: (item: PartyChipItem) => string} = {
  [ModelTypeConstants.BENCHLING_APP]: (item) => appURL(item.id),
  [ModelTypeConstants.ENTITY]: (item) => userURL(item.id),
  [ModelTypeConstants.ORGANIZATION]: (item: OrganizationNode) =>
    organizationDAO.organizationURL(item.urlIdentifier),
  [ModelTypeConstants.ROUTINE]: (item) =>
    chatSettingsModalUrl(ChatSettingsTab.AGENTS, {selectedItemId: item.id}),
  [ModelTypeConstants.TEAM]: (item) => teamURL(item.id),
};

function getPartyType(item?: PartyChipItem): string | null {
  if (!item) {
    return null;
  }

  const modelType = APIUtil.apiIdToMaybeType(item.id);
  switch (modelType) {
    case ModelTypeConstants.ENTITY:
      if (item.__typename === 'OrganizationNode') {
        return ModelTypeConstants.ORGANIZATION;
      }
      return modelType;
    case ModelTypeConstants.TEAM:
    case ModelTypeConstants.BENCHLING_APP:
    case ModelTypeConstants.ROUTINE:
      return modelType;
    default:
      assert(false, `Model type ${modelType} is not supported by PartyChip`);
  }
}

function getPartyChipUrl(item: PartyChipItem): string {
  const partyType = getPartyType(item);
  if (partyType == null) {
    return '';
  }
  return PARTY_TYPE_TO_URL_FN[partyType](item);
}

export default function PartyChip(props: SharedSmartChipProps<SmartChipType.PARTY>): JSX.Element {
  const sharedSmartChipProps = getSharedSmartChipProps(props);
  const {id} = sharedSmartChipProps;
  const {
    item: party,
    handleClickLink,
    itemUrl,
    isInaccessible,
  } = useSmartChipQuery<SmartChipType.PARTY | SmartChipType.ROUTINE>({
    id,
    getUrlFn: getPartyChipUrl,
  });

  return (
    <SmartChip
      {...sharedSmartChipProps}
      isLink={true}
      icon={getPartyType(party) as BenchlingIconName}
      item={party}
      onClick={handleClickLink}
      url={itemUrl}
      isInaccessible={isInaccessible}
    />
  );
}
